import * as THREE from 'three'
import { gsap } from 'gsap'
import { sceneConfig } from '@/_constants/scene-config'
import { clamp, damp } from '@/_utilities/math'

/**
 * Owns the perspective camera and all of its motion: a slow auto-orbit, pointer
 * parallax, scroll-wheel zoom, the cinematic intro glide, and the warp-into-planet
 * transition used when a service is entered.
 */
export class CameraRig {
  public readonly camera: THREE.PerspectiveCamera
  public warpAmount: number

  private readonly lookTarget: THREE.Vector3
  private readonly offset: THREE.Vector3
  private readonly spherical: THREE.Spherical
  private readonly baseFieldOfView: number
  private pointerX: number
  private pointerY: number
  private azimuth: number
  private polarCurrent: number
  private polarTarget: number
  private distanceCurrent: number
  private distanceTarget: number
  private introSpin: number
  private isWarping: boolean

  /**
   * @param {number} aspect - The initial viewport aspect ratio.
   */
  public constructor(aspect: number) {
    this.camera = new THREE.PerspectiveCamera(
      sceneConfig.camera.fieldOfView,
      aspect,
      sceneConfig.camera.near,
      sceneConfig.camera.far,
    )
    this.baseFieldOfView = sceneConfig.camera.fieldOfView
    this.warpAmount = 0

    this.lookTarget = new THREE.Vector3(0, 0, 0)
    this.offset = new THREE.Vector3()
    this.spherical = new THREE.Spherical()
    this.pointerX = 0
    this.pointerY = 0
    this.azimuth = 0
    this.polarCurrent = sceneConfig.camera.defaultPolarAngle
    this.polarTarget = sceneConfig.camera.defaultPolarAngle
    this.distanceCurrent = sceneConfig.camera.defaultDistance
    this.distanceTarget = sceneConfig.camera.defaultDistance
    this.introSpin = 0
    this.isWarping = false

    this.applyCameraPosition()
  }

  /**
   * Feed the rig the latest normalized pointer position for parallax.
   *
   * @param {number} normalizedX - Pointer X in the -1..1 range.
   * @param {number} normalizedY - Pointer Y in the -1..1 range.
   * @returns {void}
   */
  public setPointer(normalizedX: number, normalizedY: number): void {
    this.pointerX = normalizedX
    this.pointerY = normalizedY
  }

  /**
   * Apply a scroll-wheel delta to the zoom distance.
   *
   * @param {number} deltaY - The wheel delta (positive zooms out).
   * @returns {void}
   */
  public handleWheel(deltaY: number): void {
    if (this.isWarping) {
      return
    }
    this.distanceTarget = clamp(
      this.distanceTarget + deltaY * 0.12,
      sceneConfig.camera.minDistance,
      sceneConfig.camera.maxDistance,
    )
  }

  /**
   * Update the camera projection for a new viewport size.
   *
   * @param {number} width - The viewport width in pixels.
   * @param {number} height - The viewport height in pixels.
   * @returns {void}
   */
  public setViewport(width: number, height: number): void {
    this.camera.aspect = width / height
    this.camera.updateProjectionMatrix()
  }

  /**
   * Kick off the cinematic intro: start far out and glide in to the default frame.
   *
   * @returns {void}
   */
  public playIntro(): void {
    this.distanceCurrent = sceneConfig.camera.maxDistance * 1.7
    this.introSpin = 1.1
  }

  /**
   * Warp the camera into a body's position, then resolve once the move completes.
   *
   * @param {THREE.Vector3} position - The world position to dive toward.
   * @param {number} durationSeconds - How long the warp should take.
   * @returns {Promise<void>} Resolves when the warp finishes.
   */
  public warpTo(position: THREE.Vector3, durationSeconds: number): Promise<void> {
    this.isWarping = true
    const state = { distance: this.distanceCurrent }
    return new Promise((resolve) => {
      gsap.to(this.lookTarget, {
        x: position.x,
        y: position.y,
        z: position.z,
        duration: durationSeconds,
        ease: 'power2.inOut',
      })
      gsap.to(this, {
        warpAmount: 1,
        duration: durationSeconds,
        ease: 'power2.in',
      })
      gsap.to(this.camera, {
        fov: this.baseFieldOfView + 22,
        duration: durationSeconds,
        ease: 'power2.in',
        onUpdate: () => {
          this.camera.updateProjectionMatrix()
        },
      })
      gsap.to(state, {
        distance: sceneConfig.camera.minDistance * 0.3,
        duration: durationSeconds,
        ease: 'power2.in',
        onUpdate: () => {
          this.distanceCurrent = state.distance
        },
        onComplete: () => {
          resolve()
        },
      })
    })
  }

  /**
   * Advance the camera one frame.
   *
   * @param {number} deltaSeconds - Time since the previous frame.
   * @returns {void}
   */
  public update(deltaSeconds: number): void {
    this.introSpin = damp(this.introSpin, 0, 0.02, deltaSeconds)
    this.azimuth += (sceneConfig.camera.autoOrbitSpeed + this.introSpin * 0.5) * deltaSeconds

    if (!this.isWarping) {
      this.polarTarget = sceneConfig.camera.defaultPolarAngle - this.pointerY * 0.18
      this.polarCurrent = damp(
        this.polarCurrent,
        this.polarTarget,
        sceneConfig.camera.parallaxEase,
        deltaSeconds,
      )
      this.distanceCurrent = damp(this.distanceCurrent, this.distanceTarget, 0.05, deltaSeconds)
    }

    this.applyCameraPosition()
  }

  /**
   * Recompute the camera position from the current spherical state and look at
   * the active target, adding eased pointer parallax to the azimuth.
   *
   * @returns {void}
   */
  private applyCameraPosition(): void {
    const parallaxAzimuth = this.isWarping ? 0 : this.pointerX * 0.22
    const polar = clamp(this.polarCurrent, 0.25, Math.PI - 0.25)
    this.spherical.set(this.distanceCurrent, polar, this.azimuth + parallaxAzimuth)
    this.offset.setFromSpherical(this.spherical)
    this.camera.position.copy(this.lookTarget).add(this.offset)
    this.camera.lookAt(this.lookTarget)
  }
}
